<template>
    <div class="claim-your-home-form">
        <b-row>
            <b-col sm="6">
                <b-form-group
                        label="First name"
                        label-class="font-weight-bold pb-0"
                >
                    <b-form-input
                            v-model="internalModel.firstName"
                            :disabled="disabled"
                            type="text"
                            @input="$emit('clear-errors', 'firstName')"
                    />
                    <template v-if="errors.firstName">
                        <b-form-invalid-feedback
                                v-for="(error, errIndex) in errors.firstName"
                                :key="errIndex"
                                class="d-block"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>
                </b-form-group>
            </b-col>
            <b-col sm="6">
                <b-form-group
                        label="Last name"
                        label-class="font-weight-bold pb-0"
                >
                    <b-form-input
                            v-model="internalModel.lastName"
                            :disabled="disabled"
                            type="text"
                            @input="$emit('clear-errors', 'lastName')"
                    />
                    <template v-if="errors.lastName">
                        <b-form-invalid-feedback
                                v-for="(error, errIndex) in errors.lastName"
                                :key="errIndex"
                                class="d-block"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col sm="6">
                <b-form-group
                        label="Email Address"
                        label-class="font-weight-bold pb-0"
                >
                    <b-form-input
                            v-model="internalModel.email"
                            :disabled="disabled"
                            autocomplete="username"
                            name="email"
                            type="text"
                    />
                    <template v-if="errors.email">
                        <b-form-invalid-feedback
                                v-for="(error, errIndex) in errors.email"
                                :key="errIndex"
                                class="d-block"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>
                </b-form-group>
            </b-col>
            <b-col sm="6">
                <b-form-group>
                    <label class="tw-text-base tw-font-bold tw-mb-0 phone-input-label">Mobile phone - optional</label>
                    <b-input-group class="phone-input-group">
                        <template #prepend>
                            <b-img
                                    :src="require('../../../../../../assets/images/usa-flag.png')"
                                    alt=""
                                    class="phone-input-flag"
                            />
                        </template>

                        <b-form-input
                                v-model="phone"
                                :disabled="disabled"
                                :formatter="formatNumber"
                                autocomplete="tel"
                                class="phone-input-numbers"
                                placeholder="(   )    -    "
                                type="tel"
                        />
                    </b-input-group>
                    <template v-if="errors.phone">
                        <b-form-invalid-feedback
                                v-for="(error, errIndex) in errors.phone"
                                :key="errIndex"
                                class="d-block"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-form-group
                        class="claim-your-home-form-password-form-group"
                        label="Password"
                        label-class="font-weight-bold pb-0"
                >
                    <b-input-group
                            id="password-wrapper"
                            class="password-input-group"
                    >
                        <b-form-input
                                v-model="internalModel.password"
                                :disabled="disabled"
                                :type="passwordType"
                                autocomplete="current-password"
                                name="password"
                                @focus="showPasswordTooltip = true"
                                @focusout="showPasswordTooltip = false"
                        />
                        <b-input-group-append class="d-flex justify-content-center align-items-center">
                            <i
                                    :class="{
                                        'fa-eye' : passwordType === 'password',
                                        'fa-eye-slash' : passwordType === 'text',
                                    }"
                                    class="far text-gray-700 cursor-pointer"
                                    @click="handlePasswordEyeClick"
                            />
                        </b-input-group-append>
                    </b-input-group>

                    <password-validation
                            :password="internalModel.password"
                            :visible="isPasswordValidationVisible"
                            target="password-wrapper"
                    />

                    <template v-if="errors.password">
                        <b-form-invalid-feedback
                                v-for="(error, errIndex) in errors.password"
                                :key="errIndex"
                                class="d-block"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>
                </b-form-group>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import './ClaimYourHomeForm.scss';
import FormErrorsMixin from "@milestoneslabs/milestones-ui-kit/src/components/FormErrors/FormErrorsMixin";
import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
import VModel from "@milestoneslabs/milestones-ui-kit/src/mixins/VModel";
import PasswordValidation from "@milestoneslabs/milestones-ui-kit/src/components/Onboarding/PasswordValidationTooltip.vue";
import parsePhoneNumber from "libphonenumber-js";

export default {
    name: "ClaimYourHomeForm",
    mixins: [
        FormErrorsMixin,
        ShowNotificationMixin,
        VModel,
    ],
    components: {
        PasswordValidation
    },
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        userExists: {
            type: Boolean,
            default: false
        },
        errors: {
            type: Object,
            default: () => {
                return {};
            }
        }
    },
    data() {
        return {
            showPasswordTooltip: false,
            passwordType: 'password',
        };
    },
    computed: {
        phone: {
            get() {
                return this.internalModel.phone;
            },
            set(value) {
                const phoneNumber = parsePhoneNumber(value, 'US');
                this.internalModel.phone = this.formatNumber(phoneNumber && phoneNumber.nationalNumber ?
                    phoneNumber.nationalNumber : value);

                this.$emit('input', this.internalModel);
            }
        },

        isPasswordValidationVisible() {
            return (this.showPasswordTooltip
                || this.errors.hasOwnProperty('password'))
                && !this.userExists;
        },
    },
    methods: {
        formatNumber(value) {
            var x = value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
            value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');

            return value;
        },
        handlePasswordEyeClick() {
            this.passwordType = this.passwordType === 'password' ?
                'text' : 'password';
        }
    },
    mounted() {
        this.phone = this.formatNumber(this.phone);
    },
};
</script>
